import { useState, useEffect, useCallback, useRef, useContext } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { createUseStyles } from 'react-jss'
import CreateJs from 'preload-js'
import Meta from '@/components/Meta'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import Hero from '@/components/Hero'
import Footer from '@/components/Footer'
import useHandleAllMediaWithCb from '@/hooks/useHandleAllMediaWithCb'
import { Context } from '@/context'
import { insertBrAfterFirstSpace, cutExcerpt } from '@/utils/strings'
import actions from '@/actions'
import style from './style'

const useStyles = createUseStyles(style)

const ArchiveCategories = ({
  cpt: cptFromProps,
  stub,
}) => {
  const classes = useStyles()
  const { setPageAnimationReady, setHeaderInverse } = useContext(Context)
  const [load, setLoad] = useState(false)
  const [products, setProducts] = useState([])
  const preload = useRef(new CreateJs.LoadQueue())
  const $root = useRef()

  /*------------------------------
  Executes a callback on loading each image
  ------------------------------*/
  useHandleAllMediaWithCb({
    init: load,
    ref: $root.current?.ref,
  })

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { current, categories, page } = useSelector((state) => ({
    current: state.page.current,
    categories: state.page.taxonomies[cptFromProps],
    mediaQueryKey: state.mediaQuery.key,
    ready: state.loading.ready,
    strings: state.language.string,
    page: state.page.pages[stub] || {},
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const getPage = useCallback((index) => dispatch(actions.page.getDataPage(index)), [dispatch])
  const setCurrentPage = useCallback((index) => dispatch(actions.page.setCurrent(index)), [dispatch])
  const updatePageReady = useCallback((index) => dispatch(actions.loading.setReady(index)), [dispatch])
  const setSiteLoaded = useCallback(() => dispatch(actions.loading.setSiteLoaded()), [dispatch])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    updatePageReady(true)
    setSiteLoaded()
    setLoad(true)
  }

  /*------------------------------
  Preload Main Images: preloading only thre three first image & imageHover
  ------------------------------*/
  const preloadMedia = () => {
    preload.current.on('complete', completePreload)
    products.slice(0, 3).map((item) => {
      preload.current.loadFile({ id: `img-${item.id}`, src: item.image })
      return false
    })
  }

  /*------------------------------
  Initialize (API Calls)
  ------------------------------*/
  const initialize = () => {
    if (current !== stub) setCurrentPage(stub, 'pages')
    getPage(stub)
  }

  /*------------------------------
  If there are products & page is loaded from redux, complete the preload
  ------------------------------*/
  useEffect(() => {
    if (products.length > 0 && Object.keys(page).length > 0) preloadMedia()
  }, [products, page])
  /*------------------------------
  Map Categories to Product
  ------------------------------*/
  useEffect(() => {
    if (categories !== undefined) {
      const catMapped = categories
        .filter((category) => category.count > 0)
        .map((cat) => ({
          id: cat.id,
          title: cat.name,
          description: cat.description,
          link: cat.meta.taxonomy_external_url || cat.link,
          image: cat.meta.taxonomy_product_image_url,
        }))
      setProducts(catMapped)
    }
  }, [categories])

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
  }, [])

  /*------------------------------
  Activate Page Animation
  ------------------------------*/
  useEffect(() => {
    if (load) setPageAnimationReady(true)
  }, [load])

  /*------------------------------
  Set Header Logo Styles
  ------------------------------*/
  useEffect(() => {
    setHeaderInverse(true)
    return () => setHeaderInverse(false)
  }, [])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    return load && (
      <Meta head_json={page.yoast_head} />
    )
  }

  /*------------------------------
  Render Focus
  ------------------------------*/
  const renderFocus = (el, i) => {
    return (
      <Hero
        key={i.toString()}
        aboveTheFold={i === 0}
        background={i % 2 === 0 ? 'light' : 'dark'}
        borderTitle
        fontSize="md"
        img={el.image}
        link={el.link}
        text={cutExcerpt(el.description)}
        title={insertBrAfterFirstSpace(el.title)}
        imgSize="crop"
        render
      />
    )
  }

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = () => <Footer />

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <>
        {products.map((product, i) => renderFocus(product, i))}
        {renderFooter()}
      </>
    )
  }

  return (
    <LocomotiveScroll
      init={load}
      className={`page pageArchiveCategories ${classes.root}`}
      ref={$root}
    >
      {renderHelmet()}
      {renderContent()}
    </LocomotiveScroll>
  )
}

export default ArchiveCategories
